import type { Chat, ChatEvent } from "@codvero/shared";

/** One provider's reply to one prompt: the events after its user_prompt, up to its next one. */
export interface Segment {
  member: Chat;
  prompt: ChatEvent;
  events: ChatEvent[];
  startedAt: string;
  finishedAt: string | null;
  /** The member's newest segment (where its live, still-streaming text belongs). */
  last: boolean;
}

/** A prompt as the user sent it (once, however many providers got it) and the replies, fastest first. */
export interface Turn {
  key: string;
  kind: "prompt" | "combine";
  prompt: ChatEvent;
  /** Members the prompt went to (shown when it was not everyone). */
  recipients: Chat[];
  segments: Segment[];
  at: string;
}

const END = new Set(["run_finished", "run_error", "run_cancelled"]);
/** Prompts sent together reach every member within a moment; same text inside this window is one turn. */
const SAME_TURN_MS = 30_000;

function segmentsOf(member: Chat, events: ChatEvent[]): Segment[] {
  const out: Segment[] = [];
  for (const ev of events) {
    if (ev.type === "user_prompt") {
      out.push({ member, prompt: ev, events: [], startedAt: ev.at, finishedAt: null, last: false });
      continue;
    }
    const cur = out.at(-1);
    if (!cur) continue;
    cur.events.push(ev);
    if (END.has(ev.type)) cur.finishedAt = ev.at;
  }
  if (out.length) out[out.length - 1]!.last = true;
  return out;
}

const textOf = (ev: ChatEvent) => ((ev.payload as { text?: string } | null)?.text ?? "").trim();
const generated = (ev: ChatEvent) => !!(ev.payload as { generated?: boolean } | null)?.generated;

/**
 * Merges the members' threads into one conversation: identical prompts sent at the same moment become one turn, and
 * inside a turn the replies are ordered by who finished first (still-running ones after, by start). A combine run is
 * its own turn, placed by time.
 */
export function buildTimeline(threads: { member: Chat; events: ChatEvent[] }[]): Turn[] {
  const segs = threads.flatMap((t) => segmentsOf(t.member, t.events)).sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  const turns: Turn[] = [];
  for (const s of segs) {
    if (s.member.role === "combine" || generated(s.prompt)) {
      turns.push({ key: `c:${s.member.id}:${s.prompt.seq}`, kind: "combine", prompt: s.prompt, recipients: [s.member], segments: [s], at: s.startedAt });
      continue;
    }
    const text = textOf(s.prompt);
    const t = turns.find((x) => x.kind === "prompt" && textOf(x.prompt) === text && !x.segments.some((y) => y.member.id === s.member.id) && Math.abs(Date.parse(x.at) - Date.parse(s.startedAt)) < SAME_TURN_MS);
    if (t) {
      t.segments.push(s);
      t.recipients.push(s.member);
    } else {
      turns.push({ key: `p:${s.member.id}:${s.prompt.seq}`, kind: "prompt", prompt: s.prompt, recipients: [s.member], segments: [s], at: s.startedAt });
    }
  }
  for (const t of turns) {
    t.segments.sort((a, b) => {
      if (a.finishedAt && b.finishedAt) return a.finishedAt.localeCompare(b.finishedAt);
      if (a.finishedAt) return -1;
      if (b.finishedAt) return 1;
      return a.startedAt.localeCompare(b.startedAt);
    });
  }
  return turns.sort((a, b) => a.at.localeCompare(b.at));
}
